import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Linkedin, Twitter, Instagram, Facebook, Youtube } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { api, fileUrl } from "@/lib/api";

const SOCIAL_ICONS = {
  linkedin: Linkedin,
  twitter: Twitter,
  instagram: Instagram,
  facebook: Facebook,
  youtube: Youtube,
};

const normalizeUrl = (url) => {
  if (!url) return "";
  const trimmed = String(url).trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

export default function Footer() {
  const { t } = useLanguage();
  const year = new Date().getFullYear();
  const [settings, setSettings] = useState({});
  useEffect(() => { api.get(`/settings?_=${Date.now()}`).then(r => setSettings(r.data || {})).catch(() => {}); }, []);

  const social = settings.social || {};
  const socialLinks = Object.entries(SOCIAL_ICONS)
    .map(([key, Icon]) => ({ key, Icon, url: normalizeUrl(social[key]) }))
    .filter(x => x.url);

  const contactEmail = settings.contact_email || "info@useko.org";
  const contactPhone = settings.contact_phone || "+90 216 519 19 53";
  const contactAddress = settings.contact_address || t.contact_addr_line;

  return (
    <footer className="bg-[#102A43] text-white mt-24" data-testid="site-footer">
      <div className="useko-container py-16 md:py-20">
        <div className="grid md:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="mb-4">
              <img src={fileUrl(settings.footer_logo || settings.header_logo) || "/useko-logo-gold.png?v=4"} alt="USEKO" className="h-14 w-auto"/>
            </div>
            <p className="text-white/70 text-sm max-w-md leading-relaxed">
              {t.org_full}
            </p>
            {socialLinks.length > 0 && (
              <div className="flex items-center gap-3 mt-6" data-testid="footer-social">
                {socialLinks.map(({ key, Icon, url }) => (
                  <a
                    key={key}
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={key}
                    className="w-9 h-9 rounded-full bg-white/10 hover:bg-[#1F8A5B] flex items-center justify-center transition-colors"
                    data-testid={`social-${key}`}
                  >
                    <Icon className="w-4 h-4"/>
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Quick links */}
          <div>
            <h3 className="font-heading text-sm uppercase tracking-[0.15em] text-white/80 mb-4">{t.quick_links}</h3>
            <ul className="space-y-2.5 text-sm text-white/70">
              <li><Link className="hover:text-white transition-colors" to="/kurumsal/hakkimizda">Hakkımızda</Link></li>
              <li><Link className="hover:text-white transition-colors" to="/kurumsal/misyon-vizyon">Misyon &amp; Vizyon</Link></li>
              <li><Link className="hover:text-white transition-colors" to="/baskanin-mesaji">Başkanın Mesajı</Link></li>
              <li><Link className="hover:text-white transition-colors" to="/faaliyet-alanlari/ekonomi-ticaret">Faaliyet Alanları</Link></li>
              <li><Link className="hover:text-white transition-colors" to="/programlar/uluslararasi-is-birligi">Programlar</Link></li>
              <li><Link className="hover:text-white transition-colors" to="/haberler">Haberler</Link></li>
              <li><Link className="hover:text-white transition-colors" to="/iletisim">İletişim</Link></li>
            </ul>
          </div>

          {/* Contact + Legal */}
          <div>
            <h3 className="font-heading text-sm uppercase tracking-[0.15em] text-white/80 mb-4">{t.contact_us}</h3>
            <ul className="space-y-2.5 text-sm text-white/70">
              <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 flex-shrink-0"/>{contactAddress}</li>
              {contactPhone && <li className="flex items-start gap-2"><Phone className="w-4 h-4 mt-0.5 flex-shrink-0"/>{contactPhone}</li>}
              <li className="flex items-start gap-2"><Mail className="w-4 h-4 mt-0.5 flex-shrink-0"/>{contactEmail}</li>
            </ul>
            <h3 className="font-heading text-sm uppercase tracking-[0.15em] text-white/80 mb-4 mt-6">{t.legal}</h3>
            <ul className="space-y-2.5 text-sm text-white/70">
              <li><Link className="hover:text-white transition-colors" to="/kvkk">{t.kvkk}</Link></li>
              <li><Link className="hover:text-white transition-colors" to="/gizlilik">{t.privacy}</Link></li>
              <li><Link className="hover:text-white transition-colors" to="/cerez-politikasi">{t.cookies}</Link></li>
              <li><Link className="hover:text-white transition-colors" to="/kullanim-sartlari">{t.terms}</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/50">
          <p>© {year} USEKO. {t.all_rights}</p>
          <p>
            {t.footer_credit_prefix}{" "}
            <a
              href="https://www.dijitalroket.com"
              target="_blank"
              rel="noreferrer"
              className="text-white/80 hover:text-[#D6A84F] font-medium transition-colors"
              data-testid="dijital-roket-link"
            >
              Dijital Roket
            </a>
            {" "}{t.footer_credit_suffix}
          </p>
          <p>{t.slogan}</p>
        </div>
      </div>
    </footer>
  );
}
